import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Menu,
  MenuItem,
  IconButton,
} from '@mui/material';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import zoomPlugin from 'chartjs-plugin-zoom';
import { Line } from 'react-chartjs-2';
import DownloadIcon from '@mui/icons-material/Download';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import CloseIcon from '@mui/icons-material/Close';
import * as XLSX from 'xlsx';


ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  zoomPlugin,
  Filler
);

const GrowthAnalysisChart = ({ isOpen, onClose, csvUrl, objectMeta }) => {
  const [data, setData] = useState([]);
  const [chartData, setChartData] = useState(null);
  const [selectedObjects, setSelectedObjects] = useState(new Set());
  const [availableObjects, setAvailableObjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [downloadMenuAnchor, setDownloadMenuAnchor] = useState(null);
  const [showFilledLine, setShowFilledLine] = useState(false);

  const chartRef = useRef(null);

  const colors = [
    '#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0', '#9966FF',
    '#FF9F40', '#8E44AD', '#1ABC9C', '#2ECC71', '#3498DB'
  ];

  useEffect(() => {
    if (isOpen && csvUrl) {
      fetchAndProcessData();
    }
  }, [isOpen, csvUrl]);

  const fetchAndProcessData = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch(csvUrl);
      if (!response.ok) throw new Error('Failed to fetch growth data');

      const json = await response.json();
      const processedData = parseGrowthJSON(json);
      setData(processedData);

      const objectIds = [...new Set(processedData.map(row => row.objectId))];
      setAvailableObjects(objectIds);
      const defaultSelected = new Set(objectIds);
      setSelectedObjects(defaultSelected);

      updateChartData(processedData, defaultSelected);
    } catch (err) {
      console.error(err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Parse backend JSON: [{obj_id, frames: [{frame_idx, area_pixels, growth_rate}]}]
  const parseGrowthJSON = (jsonData) => {
    if (!Array.isArray(jsonData)) return [];
    const rows = [];
    jsonData.forEach(obj => {
      if (!Array.isArray(obj.frames)) return;
      obj.frames.forEach(f => {
        rows.push({
          frame: f.frame_idx,
          displayDateTime: `Frame ${f.frame_idx}`,
          objectId: obj.obj_id,
          area: f.area_pixels,
          growthRate: f.growth_rate ?? 0,
        });
      });
    });
    rows.sort((a, b) => a.frame - b.frame);
    return rows;
  };

  useEffect(() => {
    if (data.length) {
      updateChartData(data, selectedObjects);
    }
  }, [showFilledLine]);

  const updateChartData = (dataset, selectedSet) => {
    const datasets = Array.from(selectedSet).map((objId, index) => {
      const objectData = dataset.filter(row => row.objectId === objId);
      return {
        label: objectMeta?.[objId]?.name || `Object ${objId}`,
        data: objectData.map(row => ({
          x: row.displayDateTime,
          y: row.area,
        })),
        borderColor: colors[index % colors.length],
        backgroundColor: `${colors[index % colors.length]}40`,
        borderWidth: 2,
        tension: 0.4,
        pointRadius: 4,
        pointHoverRadius: 6,
        fill: showFilledLine,
        animations: {
          tension: {
            duration: 1000,
            easing: 'easeOutBounce',
            from: 0.3,
            to: 0.4,
            loop: true
          }
        }
      };
    });

    setChartData({ datasets });
  };

  const handleObjectToggle = (objectId) => {
    const newSelected = new Set(selectedObjects);
    if (newSelected.has(objectId)) {
      newSelected.delete(objectId);
    } else {
      newSelected.add(objectId);
    }
    setSelectedObjects(newSelected);
    updateChartData(data, newSelected);
  };

  const handleSelectAll = () => {
    const all = new Set(availableObjects);
    setSelectedObjects(all);
    updateChartData(data, all);
  };

  const handleDeselectAll = () => {
    setSelectedObjects(new Set());
    updateChartData(data, new Set());
  };

  const resetZoom = () => {
    const chart = chartRef.current;
    if (chart && chart.resetZoom) {
      chart.resetZoom();
    } else if (chart && chart.options?.plugins?.zoom) {
      chart.zoomScale('x', 1, 'default');
    }
  };

  const downloadRawData = () => {
    if (!data || !data.length) return;
    const headers = Object.keys(data[0]);
    const rows = [headers, ...data.map(row => headers.map(h => row[h] ?? ''))];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(rows), 'Growth Data');
    XLSX.writeFile(wb, 'growth_analysis_data.xlsx');
  };

  const downloadChart = () => {
    if (chartRef.current) {
      const chart = chartRef.current;
      const url = chart.toBase64Image('image/png', 1.0);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'growth_analysis_chart.png';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const chartOptions = {
    responsive: true,
    animation: {
      duration: 1200,
      easing: 'easeInOutQuart'
    },
    plugins: {
      tooltip: {
        callbacks: {
          label: (tooltipItem) => `Area: ${tooltipItem.parsed.y.toFixed(0)} px²`
        }
      },
      legend: { position: 'top' },
      title: {
        display: true,
        text: 'Growth Analysis - Area (px\u00B2) vs Frame'
      },
      zoom: {
        zoom: {
          wheel: { enabled: true },
          pinch: { enabled: true },
          mode: 'x'
        },
        pan: {
          enabled: true,
          mode: 'x'
        }
      }
    },
    scales: {
      x: {
        type: 'category',
        title: {
          display: true,
          text: 'Frame'
        },
        ticks: {
          autoSkip: true,
          maxRotation: 45,
          minRotation: 45
        }
      },
      y: {
        title: {
          display: true,
          text: 'Area (px²)'
        }
      }
    }
  };

  return isOpen ? (
    <Dialog open={isOpen} onClose={onClose} fullWidth maxWidth="xl">
      <DialogTitle sx={{ pb: 0 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Growth Analysis</Typography>
          <Box display="flex" alignItems="center" gap={1}>
            <IconButton onClick={resetZoom} size="small">
              <RestartAltIcon />
            </IconButton>
            <IconButton onClick={(e) => setDownloadMenuAnchor(e.currentTarget)} size="small">
              <DownloadIcon />
            </IconButton>
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {loading ? (
          <Typography>Loading...</Typography>
        ) : error ? (
          <Typography color="error">Error: {error}</Typography>
        ) : (
          <>
            <Box mb={2}>
              <Button onClick={handleSelectAll}>Select All</Button>
              <Button onClick={handleDeselectAll}>Deselect All</Button>
              <Button onClick={() => setShowFilledLine(prev => !prev)}>
                {showFilledLine ? 'Simple Line' : 'Filled Line'}
              </Button>
            </Box>
            <FormGroup row>
              {availableObjects.map(id => (
                <FormControlLabel
                  key={id}
                  control={<Checkbox checked={selectedObjects.has(id)} onChange={() => handleObjectToggle(id)} />}
                  label={objectMeta?.[id]?.name || `Object ${id}`}
                />
              ))}
            </FormGroup>
            <Box height={600}>
              {chartData?.datasets?.length > 0 ? (
                <Line ref={chartRef} data={chartData} options={chartOptions} />
              ) : (
                <Typography variant="body2" color="textSecondary">
                  No objects selected for chart display.
                </Typography>
              )}
            </Box>
            <Typography variant="caption" color="textSecondary">
              Tip: Use mouse wheel to zoom, drag to pan. Use the reset button above to return to full view.
            </Typography>
          </>
        )}
      </DialogContent>
      <Menu
        anchorEl={downloadMenuAnchor}
        open={Boolean(downloadMenuAnchor)}
        onClose={() => setDownloadMenuAnchor(null)}
      >
        <MenuItem
          onClick={() => {
            downloadChart();
            setDownloadMenuAnchor(null);
          }}
        >
          Download Chart (PNG)
        </MenuItem>
        <MenuItem
          onClick={() => {
            downloadRawData();
            setDownloadMenuAnchor(null);
          }}
        >
          Download Data (XLSX)
        </MenuItem>
      </Menu>
    </Dialog>
  ) : null;
};

export default GrowthAnalysisChart;
